import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarDays, Plus, Clock, Check, Trash2, Repeat, Stethoscope, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/constants";

const TYPE_META = {
  appointment: { label: "Appointment", cls: "bg-blue-100 text-blue-700", dot: "#2540FF" },
  follow_up: { label: "Follow-up", cls: "bg-amber-100 text-amber-700", dot: "#D97706" },
};
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const fmtDay = (d) => d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

const emptyForm = (day) => ({ title: "", type: "appointment", start: `${dateKey(day)}T10:00`, duration_min: 30, remind_minutes: 30, email: "", entity_type: "none", entity_name: "", owner: "", notes: "" });

export default function CalendarPage() {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(new Date()));

  const load = () => api.get("/appointments").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const eventDates = useMemo(() => events.map((e) => new Date(e.start)), [events]);
  const dayEvents = useMemo(
    () => events.filter((e) => (e.start || "").slice(0, 10) === dateKey(date)).sort((a, b) => a.start.localeCompare(b.start)),
    [events, date]
  );
  const upcoming = useMemo(() => {
    const now = new Date().toISOString();
    return events.filter((e) => e.start >= now && e.status === "scheduled").slice(0, 6);
  }, [events]);

  const openAdd = () => { setForm(emptyForm(date)); setOpen(true); };

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    if (!form.start) return toast.error("Pick a date & time");
    const payload = { ...form, duration_min: Number(form.duration_min) || 30, remind_minutes: Number(form.remind_minutes) || 30 };
    if (payload.entity_type === "none") payload.entity_type = null;
    await api.post("/appointments", payload);
    toast.success("Scheduled");
    setOpen(false); load();
  };

  const markDone = async (e) => { await api.patch(`/appointments/${e._id}`, { status: e.status === "done" ? "scheduled" : "done" }); load(); };
  const remove = async (e) => { await api.delete(`/appointments/${e._id}`); toast.success("Removed"); load(); };

  const syncUrl = `${BACKEND_URL}/api/appointments.ics?token=${localStorage.getItem("ccc_token") || ""}`;
  const downloadIcs = async () => {
    const r = await api.get("/appointments.ics", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/calendar" }));
    const a = document.createElement("a"); a.href = url; a.download = "correcore.ics"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Calendar file downloaded");
  };
  const copySync = async () => { try { await navigator.clipboard.writeText(syncUrl); toast.success("Sync link copied — add it as a subscribed calendar on your phone"); } catch { toast.message(syncUrl); } };

  const Row = ({ e }) => {
    const m = TYPE_META[e.type] || TYPE_META.appointment;
    return (
      <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={`appt-${e._id}`}>
        <div className="text-center min-w-[54px]">
          <p className="text-sm font-bold font-head text-ccc-navy">{fmtTime(e.start)}</p>
          <p className="text-[10px] text-slate-400">{e.duration_min}m</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${e.status === "done" ? "line-through text-slate-400" : "text-ccc-navy"}`}>{e.title}</p>
            <Badge className={`${m.cls} border-0 text-[10px] flex items-center gap-1`}>{e.type === "follow_up" ? <Repeat className="h-3 w-3" /> : null}{m.label}</Badge>
            {e.status === "cancelled" && <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Cancelled</Badge>}
          </div>
          {(e.entity_name || e.owner) && (
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
              {e.entity_name && <><Stethoscope className="h-3 w-3" />{e.entity_name}</>}{e.entity_name && e.owner ? " · " : ""}{e.owner}
            </p>
          )}
          {e.notes && <p className="text-xs text-slate-500 mt-1">{e.notes}</p>}
        </div>
        <div className="flex items-center gap-1">
          <button title="Toggle done" onClick={() => markDone(e)} data-testid={`appt-done-${e._id}`} className={`h-7 w-7 rounded-lg flex items-center justify-center ${e.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500 hover:text-emerald-600"}`}><Check className="h-3.5 w-3.5" /></button>
          <button title="Delete" onClick={() => remove(e)} data-testid={`appt-del-${e._id}`} className="h-7 w-7 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:text-ccc-rose"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy flex items-center gap-2" data-testid="calendar-title"><CalendarDays className="h-6 w-6 text-ccc-blue" /> Calendar</h1>
          <p className="text-sm text-slate-500 mt-1">Schedule appointments and follow-ups across your funnels.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" data-testid="calendar-sync-button" onClick={copySync}><Smartphone className="h-4 w-4 mr-1.5" /> Sync to phone</Button>
        <Button variant="outline" data-testid="calendar-ics-button" onClick={downloadIcs}><Download className="h-4 w-4 mr-1.5" /> .ics</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-appointment-button" onClick={openAdd} className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Schedule</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Schedule</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Title</Label><Input data-testid="appt-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Demo call with Dr…" className="mt-1" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="appointment">Appointment</SelectItem><SelectItem value="follow_up">Follow-up</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Duration (min)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2"><Label>Date & time</Label><Input data-testid="appt-start-input" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1" /></div>
              <div><Label>Linked to</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="clinician">Clinician</SelectItem>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Name / owner</Label><Input value={form.entity_name} onChange={(e) => setForm({ ...form, entity_name: e.target.value })} placeholder="Dr Kumar" className="mt-1" /></div>
              <div className="col-span-2"><Label>Owner</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="mt-1" /></div>
              <div><Label>Reminder (min before)</Label><Input type="number" data-testid="appt-remind-input" value={form.remind_minutes} onChange={(e) => setForm({ ...form, remind_minutes: e.target.value })} className="mt-1" /></div>
              <div><Label>Email alert to</Label><Input type="email" data-testid="appt-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@email.com" className="mt-1" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="appt-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 h-fit">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            modifiers={{ has: eventDates }}
            modifiersClassNames={{ has: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-ccc-blue" }}
            className="rounded-xl"
          />
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Upcoming</p>
            <div className="space-y-1.5">
              {upcoming.map((e) => (
                <button key={e._id} onClick={() => setDate(new Date(e.start))} className="w-full text-left text-xs flex items-center gap-2 hover:bg-slate-50 rounded-md px-2 py-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: (TYPE_META[e.type] || {}).dot }} />
                  <span className="text-slate-500">{new Date(e.start).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                  <span className="text-ccc-navy truncate">{e.title}</span>
                </button>
              ))}
              {upcoming.length === 0 && <p className="text-xs text-slate-400">Nothing scheduled.</p>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-head text-ccc-navy flex items-center gap-2"><Clock className="h-4 w-4 text-ccc-blue" /> {fmtDay(date)}</h2>
            <span className="text-xs text-slate-400">{dayEvents.length} scheduled</span>
          </div>
          <div className="space-y-3" data-testid="day-events">
            {dayEvents.map((e) => <Row key={e._id} e={e} />)}
            {dayEvents.length === 0 && (
              <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
                <CalendarDays className="h-8 w-8 text-slate-300 mx-auto" />
                <p className="text-sm text-slate-400 mt-2">No appointments or follow-ups this day.</p>
                <Button variant="outline" onClick={openAdd} className="mt-3"><Plus className="h-4 w-4 mr-1.5" /> Schedule one</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
